import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomMutation } from '@refinedev/core';
import { WizardLayout } from '@/components/wizard';
import { PricingTiersOffline, type PricingTierDraft } from '@/components/pricing-tiers';
import { SubscriptionPlanFormFields, buildPlanFormState } from './components/SubscriptionPlanFormFields';
import type { SubscriptionPlanFormData } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, DollarSign } from 'lucide-react';

const STEPS = [
  { key: 'basics', label: 'Основные данные' },
  { key: 'pricing', label: 'Тарифы' },
  { key: 'review', label: 'Обзор' },
];

export function SubscriptionPlanWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('basics');
  const [planData, setPlanData] = useState<SubscriptionPlanFormData | null>(null);
  const [pricingTiers, setPricingTiers] = useState<PricingTierDraft[]>([]);
  const submitRef = useRef<(() => void) | null>(null);

  const compositeMutation = useCustomMutation();
  const isSubmitting = compositeMutation.mutation.isPending;

  const stepIndex = STEPS.findIndex((s) => s.key === currentStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEPS.length - 1;

  const canProceed = (() => {
    if (currentStep === 'basics') {
      return true; // валидация при submit формы
    }
    if (currentStep === 'review') {
      return planData !== null;
    }
    return true;
  })();

  const handleStepClick = (key: string) => {
    const targetIndex = STEPS.findIndex((s) => s.key === key);
    if (targetIndex <= stepIndex) {
      setCurrentStep(key);
    }
  };

  const handleNextStep = () => {
    if (currentStep === 'basics') {
      submitRef.current?.();
      return;
    }
    const nextIndex = stepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex].key);
    }
  };

  const handlePrevStep = () => {
    const prevIndex = stepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex].key);
    }
  };

  const handlePlanDataSubmit = async (data: SubscriptionPlanFormData) => {
    setPlanData(data);
    setCurrentStep('pricing');
  };

  const handleComplete = async () => {
    if (!planData) {
      return;
    }

    const mappedTiers = pricingTiers.map((t) => ({
      durationDays: t.durationDays,
      prices: t.prices,
      baseCurrency: t.baseCurrency,
      isActive: t.isActive,
      order: t.order,
      isFamily: t.isFamily,
      familySlots: t.familySlots,
      imageId: t.imageId,
      gradientFrom: t.gradientFrom,
      gradientTo: t.gradientTo,
      gradientAngle: t.gradientAngle,
      cardAppearance: t.cardAppearance,
      translations: t.translations.filter((tr) => tr.name),
      bonusTemplates: t.bonusTemplates.map((b) => ({
        type: b.type,
        initialValue: b.initialValue,
        durationMode: b.durationMode,
        durationDays: b.durationDays,
        expiresAt: b.expiresAt,
        order: b.order,
        translations: b.translations.filter((tr) => tr.name),
      })),
    }));

    try {
      await compositeMutation.mutateAsync({
        url: '/subscription-plans/admin/composite',
        method: 'post',
        values: {
          ...planData,
          pricingTiers: mappedTiers.length > 0 ? mappedTiers : undefined,
        },
      });
      navigate('/subscription-plans');
    } catch {
      // toast handled by Refine
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <SubscriptionPlanFormFields
            key="wizard-create"
            isEdit={false}
            formLoading={false}
            initialState={buildPlanFormState()}
            renderFooter={(submit) => {
              submitRef.current = submit;
              return null;
            }}
            onFinish={handlePlanDataSubmit}
          />
        );

      case 'pricing':
        return (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold">Тарифы</h2>
              <p className="text-sm text-muted-foreground">
                Добавьте тарифы для абонемента. Этот шаг необязателен — тарифы можно добавить позже.
              </p>
            </div>
            <PricingTiersOffline value={pricingTiers} onChange={setPricingTiers} showFamily />
          </div>
        );

      case 'review':
        return <ReviewStep planData={planData} pricingTiers={pricingTiers} onStepClick={setCurrentStep} />;

      default:
        return null;
    }
  };

  return (
    <WizardLayout
      title="Создать абонемент"
      steps={STEPS}
      currentStep={currentStep}
      onStepClick={handleStepClick}
      onBack={() => navigate('/subscription-plans')}
      onPrevStep={handlePrevStep}
      onNextStep={handleNextStep}
      onComplete={handleComplete}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      canProceed={canProceed}
      isSubmitting={isSubmitting}
    >
      {renderStep()}
    </WizardLayout>
  );
}

interface ReviewStepProps {
  planData: SubscriptionPlanFormData | null;
  pricingTiers: PricingTierDraft[];
  onStepClick: (key: string) => void;
}

function ReviewStep({ planData, pricingTiers, onStepClick }: ReviewStepProps) {
  if (!planData) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Обзор</h2>
      <p className="text-sm text-muted-foreground">Проверьте данные перед созданием абонемента.</p>

      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('basics')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-4 w-4" />
              Основные данные
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          <p><span className="text-muted-foreground">Код:</span> {planData.code}</p>
        </CardContent>
      </Card>

      <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => onStepClick('pricing')}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Тарифы
            </CardTitle>
            <Badge variant="outline">Редактировать</Badge>
          </div>
        </CardHeader>
        <CardContent className="text-sm">
          {pricingTiers.length === 0 ? (
            <p className="text-muted-foreground">Тарифы не добавлены</p>
          ) : (
            <p>{pricingTiers.length} тариф(ов)</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
